// Defaults & Workout rebuild — the workspace status banner (five-second
// rule): case id, the 6-stage chip ladder (presentational rollup of the
// 21-state machine — see caseStages.ts), days in the current stage,
// trigger, the always-visible money strip (Exposure · Costs · Recoveries ·
// Net, severity-tinted), and the Transition State control.

import { Badge } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type {
  DefaultCaseDto,
  WorkoutEventDto,
  WorkoutRecoveryEntryDto,
} from "@/api/portfolioAdmin/pr4bTypes";
import { formatCalendarDate } from "@/utils/datetime";
import {
  CASE_STAGES,
  formatCaseStatus,
  stageForStatus,
  stageIndex,
} from "./caseStages";
import { TransitionStateControl } from "./TransitionStateControl";

interface CaseStatusBannerProps {
  defaultCase: DefaultCaseDto;
  loanId: string;
  totalCosts: number;
  /** The case recovery ledger — net recoveries + ratio derive from it
   *  (same math as the server totals endpoint: net / original exposure). */
  recoveries: WorkoutRecoveryEntryDto[];
  /** The case event stream — the latest state-transition stamp anchors
   *  "days in current stage". */
  events: WorkoutEventDto[];
}

const fmtCurrency = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : formatCurrency(n);

function daysSince(iso: string): number {
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return 0;
  return Math.max(0, Math.floor((Date.now() - ms) / 86_400_000));
}

/** The date the case entered its current stage = the newest
 *  state-transition event (incl. the opening event), else the trigger. */
export function currentStageEnteredAt(
  defaultCase: DefaultCaseDto,
  events: WorkoutEventDto[],
): string {
  const transitions = events
    .filter((e) => e.eventCategory === "state-transition")
    .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
  return transitions[0]?.eventDate ?? defaultCase.triggerDate;
}

function StageChipLadder({ status }: { status: string }) {
  const current = stageForStatus(status);
  const currentIx = stageIndex(current);
  return (
    <ol
      className="flex items-center gap-space-1 flex-wrap m-0 p-0 list-none"
      data-testid="case-stage-ladder"
      aria-label="Case stage"
    >
      {CASE_STAGES.map((s, i) => {
        const state = i < currentIx ? "done" : i === currentIx ? "current" : "todo";
        return (
          <li key={s.key} className="flex items-center gap-space-1">
            <span
              data-stage={s.key}
              data-state={state}
              className={`inline-flex items-center px-space-2 h-6 rounded-full text-caption font-bold ${
                state === "current"
                  ? "bg-onyx text-white-pure"
                  : state === "done"
                    ? "bg-white-warm text-onyx border border-border-light"
                    : "bg-white-warm text-gray-mid border border-border-light"
              }`}
              aria-current={state === "current" ? "step" : undefined}
            >
              {s.label}
            </span>
            {i < CASE_STAGES.length - 1 && (
              <span className="w-4 h-[2px] bg-border-light" aria-hidden />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function MoneyCell({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: string;
  tone?: "error" | "success" | "neutral";
  testId: string;
}) {
  const toneClass =
    tone === "error"
      ? "text-status-error"
      : tone === "success"
        ? "text-status-success"
        : "text-onyx";
  return (
    <div className="min-w-0">
      <p className="m-0 text-caption text-gray-mid uppercase tracking-button">{label}</p>
      <p
        className={`m-0 text-small font-bold tabular-nums ${toneClass}`}
        data-testid={testId}
      >
        {value}
      </p>
    </div>
  );
}

export function CaseStatusBanner({
  defaultCase,
  loanId,
  totalCosts,
  recoveries,
  events,
}: CaseStatusBannerProps) {
  const exposure = defaultCase.originalExposure ?? 0;
  const netRecoveries = recoveries.reduce((s, r) => s + r.netAmount, 0);
  const netPosition = netRecoveries - totalCosts;
  const ratio = exposure === 0 ? 0 : netRecoveries / exposure;
  const stageDays = daysSince(currentStageEnteredAt(defaultCase, events));

  return (
    <div
      className="border border-border-light rounded-md bg-white-pure p-space-3 flex flex-col gap-space-3"
      data-testid="case-status-banner"
    >
      <div className="flex items-start justify-between gap-space-3 flex-wrap">
        <div className="flex flex-col gap-space-2 min-w-0">
          <div className="flex items-center gap-space-2 flex-wrap">
            <span className="font-mono text-small font-bold">
              {defaultCase.id.slice(0, 8).toUpperCase()}
            </span>
            <Badge status="error">{formatCaseStatus(defaultCase.status)}</Badge>
            {defaultCase.currentRemediationLevel && (
              <Badge status="info">
                Level {defaultCase.currentRemediationLevel}
              </Badge>
            )}
            <Badge status="draft">
              {stageDays}d in stage • {daysSince(defaultCase.triggerDate)}d open
            </Badge>
          </div>
          <StageChipLadder status={defaultCase.status} />
          <p className="text-small text-gray-mid m-0">
            Triggered: <strong>{defaultCase.triggeredBy ?? "—"}</strong> •{" "}
            {formatCalendarDate(defaultCase.triggerDate)}
          </p>
        </div>
        <div className="shrink-0">
          <TransitionStateControl
            caseId={defaultCase.id}
            loanId={loanId}
            currentState={defaultCase.status}
          />
        </div>
      </div>

      <div
        className="flex flex-wrap gap-space-4 border-t border-border-light pt-space-2"
        data-testid="case-money-strip"
      >
        <MoneyCell label="Exposure" value={fmtCurrency(exposure)} testId="case-money-exposure" />
        <MoneyCell
          label="Costs"
          value={fmtCurrency(totalCosts)}
          tone={totalCosts > 0 ? "error" : "neutral"}
          testId="case-money-costs"
        />
        <MoneyCell
          label="Recoveries (net)"
          value={fmtCurrency(netRecoveries)}
          tone={netRecoveries > 0 ? "success" : "neutral"}
          testId="case-money-recoveries"
        />
        <MoneyCell
          label="Net position"
          value={fmtCurrency(netPosition)}
          tone={netPosition >= 0 ? "success" : "error"}
          testId="case-money-net"
        />
        <MoneyCell
          label="Recovery ratio"
          value={`${(ratio * 100).toFixed(1)}%`}
          testId="case-money-ratio"
        />
      </div>
    </div>
  );
}
